import { useEffect, useRef, useState } from 'react';
import FeatureFlagModule from '..';

// not unused, used in the tsdoc
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import type { useFeatureFlag } from './useFeatureFlag/useFeatureFlag';
import { FeatureFlagKeys, isValidFeatureFlagKey } from '../featureFlagKeys';

interface UseFeatureFlagsOptions {
  /**
   * The default values/default value function for the feature flags.
   * If a flag has no value, the hook will return the default value if provided.
   */
  defaultValues?:
    | Partial<Record<FeatureFlagKeys, unknown>>
    | ((flagKey: FeatureFlagKeys) => unknown);
}

/**
 * A hook that can be used to subscribe to the values of one or more feature flags.
 * When a flag value changes, the hook will force a re-render with the new value.
 * This hook is useful for components that need to react to feature flag changes, particularly when multiple flags are needed.
 * If a single flag is needed, use {@link useFeatureFlag} instead to reduce overhead and complexity.
 * If a flag has no value, the hook will return undefined for that flag or a default value if {@link UseFeatureFlagsOptions.defaultValues} is provided for that flag.
 *
 * @returns A proxy object with the feature flags as properties which force a re-render whenever any of the requested flags change.
 * @example
 * const {
 *   [FeatureFlagKeys.testFlag]: testFlagValue,
 *   [FeatureFlagKeys.anotherFlag]: anotherFlagValue
 * } = useFeatureFlags();
 *
 * return (
 *   <div>
 *     <div>{testFlagValue ? 'Test flag is enabled' : 'Test flag is disabled'}</div>
 *     <div>{anotherFlagValue ? 'Another flag is enabled' : 'Another flag is disabled'}</div>
 *   </div>
 * );
 */
export default function useFeatureFlags(options?: UseFeatureFlagsOptions) {
  const { defaultValues } = options || {};
  const [, setUpdateCount] = useState(0);
  const cleanupFunctionsRef = useRef<Map<FeatureFlagKeys, () => void>>(
    new Map(),
  );
  const forceUpdateRef = useRef(() => setUpdateCount((c) => c + 1));

  const defaultValuesRef = useRef(defaultValues);
  defaultValuesRef.current = defaultValues;

  // Keep the forceUpdate ref current
  forceUpdateRef.current = () => setUpdateCount((c) => c + 1);

  // Cleanup all listeners on unmount
  useEffect(() => {
    const cleanupFunctions = cleanupFunctionsRef.current;
    return () => {
      cleanupFunctions.forEach((cleanupFunction) => cleanupFunction());
      cleanupFunctions.clear();
    };
  }, []);

  const proxyRef = useRef(
    new Proxy({} as Record<FeatureFlagKeys, unknown>, {
      get(_target, prop: string) {
        if (typeof prop !== 'string') return undefined;

        // must be a valid FeatureFlagKeys
        if (!isValidFeatureFlagKey(prop)) return undefined;

        // Register listener if not already subscribed to this flag
        if (!cleanupFunctionsRef.current.has(prop)) {
          const { remove } = FeatureFlagModule.addFlagListener(prop, () => {
            forceUpdateRef.current();
          });
          cleanupFunctionsRef.current.set(prop, remove);
        }

        const { value, hasValue } = FeatureFlagModule.getFeatureFlagValue(prop);

        if (!hasValue) {
          if (typeof defaultValuesRef.current === 'function') {
            return defaultValuesRef.current(prop);
          }
          return defaultValuesRef.current?.[prop];
        }

        return value;
      },
    }),
  );

  return proxyRef.current;
}
